import { useState, useMemo, useCallback, useEffect } from 'react'
import { Collapse, Select, Modal, Popover, notification, Button } from 'antd'
import {
  PlusOutlined,
  MinusOutlined,
  SettingOutlined,
  FileAddOutlined,
  EyeInvisibleOutlined,
  AppstoreAddOutlined,
  CloseSquareOutlined,
  ReadOutlined,
} from '@ant-design/icons'
import SeasonRuleTable from '../../../Components/Omnichannel/SeasonRuleTable'
import { TranslationKeys } from '../../../Constants/TranslationKeys'
import { isProdSite, renderTranslation, stopPropagation } from '../../../Helpers/GenericHelpers'

import { copyEntireSeason, addNewSeasonToSeasonList } from '../../../Services/Omnichannel/SeasonRuleService'
import { retrieveAvailableSeasons, retrieveRule, retrieveRuleFromSubrule } from '../../../Helpers/DiscountHelper'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import { NavLink, generatePath, useParams } from 'react-router-dom'
import { CLIENT_PAGES_PATH } from '../../../Constants/ClientRoutes'
import { setSubRuleSection } from '../../../Slices/Omnichannel/SeasonRuleSlice'
import { useNavigateWrapper } from '../../../Helpers/NavigateHelper'
import { TypeOfChannel } from 'Slices/Omnichannel/DiscountSlice'
import WalletIcon from 'Components/Svg/WalletIcon'
import OmnichannelLogs from './OmnichannelLogs'
import LogIcon from 'Components/Svg/LogIcon'

function SeasonRuleList(props) {
  const dispatch = useAppDispatch()
  const navigationWrapper = useNavigateWrapper()
  const language = useAppSelector(state => state.present.language.value)
  const seasonList = useAppSelector(state => state.present.seasonRule.seasonList)
  const seasonRuleList = useAppSelector(state => state.present.seasonRule.seasonRuleList)
  const ruleId = useAppSelector(state => state.present.seasonRule.ruleId)
  const subRuleId = useAppSelector(state => state.present.subRule.subRuleId)
  const typeChannel = useAppSelector(state => state.present.discount.typeOfChannel)
  const isOmnichannelAndPricesVisibile = useAppSelector(
    state => state.present.configuration.isOmnichannelAndPricesVisibile,
  )
  const settingsVisibility = useAppSelector(state => state.present.configuration.settingsVisibility)

  const [activeKey, setActiveKey] = useState([])
  const [seasonModal, setSeasonModal] = useState(null)
  const [seasonRuleCopy, setSeasonRuleCopy] = useState(null)
  const [addSeasonActive, setAddSeasonActive] = useState(false)
  const [seasonSelected, setSeasonSelected] = useState(null)
  const [showSeasonActive, setShowSeasonActive] = useState(false)
  const [showOmnichannelLogModal, setShowOmnichannelLogModal] = useState(false)

  const { id } = useParams()

  const seasonrulesFilteredByChannelType = useMemo(() => {
    //before store show alwaysOnSale
    return seasonRuleList.filter(x => !x?.season?.alwaysOnSale)
  }, [seasonRuleList, typeChannel])

  const availableSeasons = useMemo(() => {
    return retrieveAvailableSeasons(seasonList, seasonrulesFilteredByChannelType)
  }, [seasonList, seasonrulesFilteredByChannelType])

  const addSeason = useCallback(
    season => {
      if (!season) {
        notification.open({
          description: renderTranslation(language, TranslationKeys.missSelectSeasonWarning),
          message: '',
        })

        return
      }

      //add a new season
      if (availableSeasons.length !== 0) {
        dispatch(addNewSeasonToSeasonList(season, availableSeasons, navigationWrapper))
        setActiveKey(['season-collapse-table-' + season])
      }

      setAddSeasonActive(false)
      setSeasonSelected(null)
    },
    [availableSeasons, dispatch, language],
  )
  const redirectAddSeason = useCallback(
    seasonid => {
      return !availableSeasons.find(x => x.id === seasonid)?.rulesCreated ? addSeason(seasonid) : null
    },
    [availableSeasons, addSeason],
  )
  const changeActiveKey = useCallback((key: string[]) => {
    setActiveKey(key)
  }, [])

  const restoreSeason = useCallback(
    seasonSelected => {
      dispatch(addNewSeasonToSeasonList(seasonSelected, undefined, navigationWrapper))
      setShowSeasonActive(false)
      setSeasonSelected(null)
      changeActiveKey(['season-collapse-table-' + seasonSelected])
    },
    [dispatch, seasonSelected, changeActiveKey],
  )

  const copyRule = useCallback(seasonRules => {
    if (seasonRules.rules.length === 0) {
      return
    }

    // copy current season rules
    setSeasonRuleCopy(seasonRules)
  }, [])

  const onSeasonChangeModal = useCallback(
    value => {
      const season = seasonList.find(x => x.id === value)
      setSeasonModal(season)
    },
    [seasonList],
  )

  const copySeason = useCallback(() => {
    dispatch(copyEntireSeason(seasonModal, seasonRuleCopy))
    setSeasonRuleCopy(null)
    setSeasonModal(null)
  }, [dispatch, seasonModal, seasonRuleCopy])

  const closeSeasonModal = useCallback(() => {
    setSeasonRuleCopy(null)
    setSeasonModal(null)
  }, [])

  useEffect(() => {
    if (seasonrulesFilteredByChannelType.length > 0 && id && activeKey.length === 0) {
      const rule = subRuleId
        ? retrieveRuleFromSubrule(subRuleId, seasonrulesFilteredByChannelType)
        : retrieveRule(ruleId, seasonrulesFilteredByChannelType)
      if (rule && Object.keys(rule).length > 0) {
        const collapseKeyName = 'season-collapse-table-' + rule.season.id
        setActiveKey(oldArray => {
          if (oldArray.indexOf(collapseKeyName) === -1) {
            return [...oldArray, collapseKeyName]
          }

          return oldArray
        })
        if (subRuleId) {
          dispatch(setSubRuleSection(rule.id))
        }
      }
    }
  }, [seasonrulesFilteredByChannelType, id])

  return props.seasonListSectionOpen ? (
    <div className='omni-filter-container'>
      <div className='omni-filter-head slider-collapsible-head' style={{ marginBottom: addSeasonActive ? 0 : 12 }}>
        <div>{renderTranslation(language, TranslationKeys.seasonBoxSeasonRuleHead)}</div>

        {!showSeasonActive ? (
          <div>
            {!addSeasonActive ? (
              <Popover content={renderTranslation(language, TranslationKeys.seasonBoxPopoverAddSeason)} trigger='hover'>
                <AppstoreAddOutlined
                  className='generic-icon add-season-icon'
                  onClick={() => {
                    setAddSeasonActive(!addSeasonActive)
                  }}
                />
              </Popover>
            ) : (
              <div className='text-no-wrap'>
                <Select
                  className='custom-select'
                  value={seasonSelected}
                  showSearch
                  placeholder={renderTranslation(language, TranslationKeys.seasonBoxPopoverAddSeason)}
                  optionFilterProp='label'
                  onChange={e => {
                    setSeasonSelected(e)
                    redirectAddSeason(e)
                  }}
                  onClick={stopPropagation}
                  options={availableSeasons
                    .filter(e => !e.rulesCreated)
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(season => {
                      return {
                        label: season.name,
                        value: season.id,
                      }
                    })}
                  autoFocus={true}
                  defaultOpen={true}
                />
                <CloseSquareOutlined
                  className='generic-icon'
                  onClick={() => {
                    setAddSeasonActive(false)
                  }}
                />
              </div>
            )}
          </div>
        ) : (
          <></>
        )}

        {!addSeasonActive ? (
          <div>
            {!showSeasonActive ? (
              <Popover
                content={renderTranslation(language, TranslationKeys.seasonBoxPopoverShowSeason)}
                trigger='hover'
              >
                <EyeInvisibleOutlined
                  className='generic-icon add-season-icon'
                  onClick={() => {
                    setShowSeasonActive(!showSeasonActive)
                  }}
                />
              </Popover>
            ) : (
              <div className='text-no-wrap'>
                <Select
                  className='custom-select'
                  value={seasonSelected}
                  showSearch
                  placeholder={renderTranslation(language, TranslationKeys.seasonBoxPopoverShowSeason)}
                  optionFilterProp='label'
                  onChange={e => {
                    setSeasonSelected(e)
                    restoreSeason(e)
                  }}
                  onClick={stopPropagation}
                  options={availableSeasons
                    .filter(e => e.rulesCreated)
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(season => {
                      return {
                        label: season.name,
                        value: season.id,
                      }
                    })}
                  autoFocus={true}
                  defaultOpen={true}
                />
                <CloseSquareOutlined
                  className='generic-icon'
                  onClick={() => {
                    setShowSeasonActive(false)
                  }}
                />
              </div>
            )}
          </div>
        ) : (
          <></>
        )}
      </div>
      {addSeasonActive || showSeasonActive ? (
        <div className='d-flex-c justify-space-around'>
          {seasonSelected ? (
            <div className='d-flex-c cursor-pointer pa-2' onClick={() => addSeason(seasonSelected)}>
              <FileAddOutlined className='generic-icon add-season-rule-icon' />
              <div>{renderTranslation(language, TranslationKeys.createEmptySeason)}</div>
            </div>
          ) : null}
          {availableSeasons.find(x => x.id === seasonSelected)?.rulesCreated ? (
            <div className='d-flex-c cursor-pointer pa-2' onClick={restoreSeason}>
              <FileAddOutlined className='generic-icon add-season-rule-icon' />
              <div>{renderTranslation(language, TranslationKeys.restoreSeason)}</div>
            </div>
          ) : null}
        </div>
      ) : null}

      <div>
        <Modal
          title={
            renderTranslation(language, TranslationKeys.seasonModalCopySeasonTitle) +
            ' ' +
            (seasonRuleCopy ? seasonRuleCopy.season.name : '')
          }
          open={seasonRuleCopy !== null}
          onOk={copySeason}
          onCancel={closeSeasonModal}
          width={400}
          className='text-center'
        >
          <div>{renderTranslation(language, TranslationKeys.seasonModalCopySeasonBody)}</div>
          <Select
            className='custom-select season-rule-select w-180-p'
            value={seasonModal ? seasonModal.id : null}
            showSearch
            optionFilterProp='label'
            onChange={onSeasonChangeModal}
            onClick={stopPropagation}
            options={availableSeasons
              .filter(e => !e.rulesCreated)
              .map(season => {
                return {
                  value: season.id,
                  label: season.name,
                }
              })}
          />
        </Modal>
        <Collapse
          activeKey={activeKey}
          onChange={changeActiveKey}
          expandIcon={panelProps => {
            return panelProps.isActive ? <MinusOutlined key={'icon-minus'} /> : <PlusOutlined key={'icon-plus'} />
          }}
        >
          {seasonrulesFilteredByChannelType.map((seasonObj, index) => {
            return (
              <SeasonRuleTable
                key={'season-collapse-table-' + seasonObj.season.id}
                seasonObj={seasonObj}
                index={index}
                copyRule={copyRule}
                seasonrulelist={seasonrulesFilteredByChannelType}
                collapsible={!seasonObj.season.outlet ? 'icon' : seasonObj.season.outlet ? 'icon' : 'disabled'}
              />
            )
          })}
        </Collapse>
      </div>
      {isOmnichannelAndPricesVisibile && settingsVisibility ? (
        <div>
          <NavLink className='channel-setting-button' to={CLIENT_PAGES_PATH.ChannelSettings}>
            <SettingOutlined className='generic-icon channel-setting-icon mr-5' />
            {renderTranslation(language, TranslationKeys.btnChannelsSetting)}
          </NavLink>
          {!isProdSite() ? (
            <NavLink className='channel-setting-button' to={CLIENT_PAGES_PATH.Expenses}>
              <WalletIcon style={{ marginRight: '20px', width: '25px', height: '25px' }} />
              Registra spese
            </NavLink>
          ) : null}
          {!isProdSite() ? (
            <div
              className='channel-setting-button cursor-pointer'
              onClick={() => {
                setShowOmnichannelLogModal(true)
              }}
            >
              <LogIcon style={{ marginRight: '20px', width: '25px', height: '25px' }} />
              Omnichannel Log
            </div>
          ) : null}
        </div>
      ) : null}
      <OmnichannelLogs
        isVisible={showOmnichannelLogModal}
        onCancel={() => {
          setShowOmnichannelLogModal(false)
        }}
      ></OmnichannelLogs>
    </div>
  ) : null
}

export default SeasonRuleList
